import { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, ShieldCheck, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

export default function Contact() {
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Forside",
        "item": "https://detekniq.dk/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Kontakt",
        "item": "https://detekniq.dk/kontakt"
      }
    ]
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: null, message: '' });

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi vender tilbage inden for 24 timer.',
        });
        e.target.reset();
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (err) {
      setFormStatus({
        type: 'error',
        message: 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Kontakt"
        description="Kontakt D.E. TekniQ K/S i Kjellerup. Ring på +45 86 66 77 00 eller send en mail til post@detekniq.dk. Vi glæder os til at samarbejde med dig!"
        schema={contactSchema}
      />
      {/* Header Banner */}
      <div className="bg-navy-950 py-16 text-white md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-energy-400">Kontakt os</span>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl mt-3">
            Vi glæder os til at samarbejde med dig!
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-navy-200">
            Uanset om du har brug for akut elektrikerhjælp, rådgivning om varmepumper eller et uforpligtende tilbud, er vi klar til at hjælpe.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Left Column: Contact Information */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Kontaktinformation</span>
              <h2 className="text-2xl font-bold text-navy-950 mt-2">Vores kontor i Kjellerup</h2>
              <p className="text-sm text-graphite-600 mt-4 leading-relaxed">
                Du er altid velkommen til at kontakte os eller kigge forbi vores kontor. Vores kørende servicemontører dækker hele regionen.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="rounded-none bg-energy-50 p-3 text-energy-600">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-navy-950">Adresse</h4>
                  <a
                    href="https://www.google.com/maps/place/N%C3%B8rrevangsvej%205,%208620%20Kjellerup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-graphite-600 hover:text-energy-600 transition-colors mt-1 block"
                  >
                    Nørrevangsvej 5, 8620 Kjellerup
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="rounded-none bg-energy-50 p-3 text-energy-600">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-navy-950">Telefon</h4>
                  <a href="tel:+4586667700" className="text-sm text-graphite-600 hover:text-energy-600 transition-colors mt-1 block whitespace-nowrap">
                    +45 86 66 77 00
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="rounded-none bg-energy-50 p-3 text-energy-600">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-navy-950">E-mail</h4>
                  <a href="mailto:post@detekniq.dk" className="text-sm text-graphite-600 hover:text-energy-600 transition-colors mt-1 block whitespace-nowrap">
                    post@detekniq.dk
                  </a>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="rounded-none border border-navy-100 bg-navy-50/30 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-navy-950 flex items-center gap-2">
                <Clock className="h-5 w-5 text-energy-600" />
                Vores åbningstider
              </h3>
              <div className="mt-4 space-y-3 text-sm text-graphite-700">
                <div className="flex justify-between border-b border-navy-100/50 pb-2">
                  <span className="font-medium">Mandag – Torsdag</span>
                  <span>07:30 – 16:00</span>
                </div>
                <div className="flex justify-between border-b border-navy-100/50 pb-2">
                  <span className="font-medium">Fredag</span>
                  <span>07:30 – 13:00</span>
                </div>
                <div className="flex justify-between text-graphite-400">
                  <span>Lørdag – Søndag</span>
                  <span>Lukket</span>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-1.5 rounded-none bg-navy-50 px-4 py-2 text-xs font-semibold text-navy-950 border border-navy-100">
                <ShieldCheck className="h-4 w-4 text-energy-600" />
                <span>ISO Certificeret</span>
              </div>
              <div className="flex items-center gap-1.5 rounded-none bg-navy-50 px-4 py-2 text-xs font-semibold text-navy-950 border border-navy-100">
                <CheckCircle2 className="h-4 w-4 text-energy-600" />
                <span>Medlem af TEKNIQ</span>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="rounded-none border border-navy-100 bg-white p-8 shadow-xl">
              <h3 className="text-xl font-bold text-navy-950">Få et uforpligtende tilbud inden for 24 timer</h3>
              <p className="text-sm text-graphite-600 mt-2">Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt.</p>

              <form onSubmit={handleFormSubmit} className="mt-8 space-y-6">
                {/* Antispam Honeypot Field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-navy-950">Navn</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-navy-950">Telefonnummer</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                      placeholder="Dit telefonnummer"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-navy-950">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                    placeholder="din.email@adresse.dk"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-navy-950">Beskrivelse</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    required
                    className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                    placeholder="Beskriv din opgave, dit projekt eller hvad du ønsker et tilbud på..."
                  ></textarea>
                </div>

                {formStatus.message && (
                  <div className={`rounded-none p-4 text-sm font-medium ${formStatus.type === 'success' ? 'bg-energy-50 text-energy-800' : 'bg-red-50 text-red-800'}`}>
                    {formStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-none bg-energy-600 py-4 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-energy-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-energy-500 disabled:opacity-50"
                >
                  {submitting ? 'Sender...' : 'Send besked'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Google Maps Section */}
      <div className="border-t border-navy-100 bg-navy-50/30 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Find os her</span>
            <h2 className="mt-3 text-2xl font-bold text-navy-950 sm:text-3xl">
              Besøg vores kontor i Kjellerup
            </h2>
          </div>
          <div className="w-full h-[450px] shadow-lg border border-navy-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d40119.80202710869!2d9.40655482561099!3d56.34659899806304!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464bee3329258b17%3A0x847592c2184ff648!2sD.E.%20TekniQ%20-%20Elektriker%20i%20Kjellerup%20%26%20Silkeborg!5e0!3m2!1sen!2sdk!4v1781251839222!5m2!1sen!2sdk"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="D.E. TekniQ Google Map"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
}
